/**
 * Le picker d'emojis de l'épreuve — unicode seulement, et PUR.
 *
 * Kayla, 18-08 : « remets le bouton emoji, un simple picker unicode suffit ».
 *
 * ⛔ POURQUOI PAS LE VRAI `EmojiPicker`. Il monte trois onglets — Emoji,
 * Stickers, GIFs — et tire `useCustomEmojiCatalog()`, une requête
 * AUTHENTIFIÉE, plus les emojis Premium de la créatrice via `creatorId`. Un
 * candidat n'a ni compte ni créatrice : les deux tiers du picker seraient
 * morts, et le premier onglet chargerait dans le vide. C'est le même mur que
 * le composeur de transaction et que le `Composer` lui-même.
 *
 * ✅ MAIS LE CLAVIER EST LE VRAI. Les emojis viennent de `emojiCatalogue`, le
 * module extrait d'`EmojiPicker` — donc les six catégories, dans le même ordre,
 * avec les mêmes 256 emojis. Une liste recopiée ici aurait divergé au premier
 * ajout, et le candidat se serait entraîné sur un clavier qui n'est pas celui
 * du poste. C'est le critère qu'on applique partout dans cette épreuve : ce qui
 * ne dépend d'aucune donnée se réutilise, le reste se copie.
 *
 * 🧩 LE VOLET EST CELUI DE LA LIBRAIRIE. Ce fichier ne dessine que le CONTENU — les six
 * catégories (`Onglets`) et la grille de boutons-emoji. Le bouton « Emoji » et son `Volet`
 * (ancré sur le bouton, porté sur <body>, Échap et clic dehors ferment, au-dessus du bouton
 * si la place manque dessous) sont ceux de `Conversation.Compositeur` : la mécanique de
 * `useVolet` est là-bas, pas recopiée ici.
 *
 * 🪤 L'insertion se fait AU CURSEUR, pas en fin de champ. Un chatteur écrit
 * « t'es chaud ce soir » puis revient poser un 😏 après « chaud » — coller en
 * bout aurait donné « t'es chaud ce soir😏 », et il aurait cessé de s'en
 * servir. `selectionStart` est lu sur le textarea au moment du clic (`insererEmoji`
 * de `VenteScreen`) : le volet reste ouvert, on peut en poser plusieurs.
 */
import { useState } from "react";

import { CATEGORIES } from "@/panels/fan/emojiCatalogue";
import { Button, Onglets } from "@/shared/ui";

export function EmojisSimples({ onChoisir }: { onChoisir: (emoji: string) => void }) {
  // 🪤 Le type explicite est obligatoire : `CATEGORIES` est `as const`, donc
  // `CATEGORIES[0].key` s'infère en `"smileys"` — le littéral, pas l'union.
  // Sans lui, cliquer un autre onglet ne compile pas.
  type CleCategorie = (typeof CATEGORIES)[number]["key"];
  const [categorie, setCategorie] = useState<CleCategorie>(CATEGORIES[0]!.key);

  const active = CATEGORIES.find((c) => c.key === categorie) ?? CATEGORIES[0]!;

  return (
    <div className="flex w-[304px] max-w-full flex-col gap-2">
      {/* Les six catégories : un rail de segments, l'emoji EST le libellé. */}
      <Onglets
        style="segments"
        taille="md"
        pleineLargeur
        nom="Catégories d'emojis"
        valeur={active.key}
        onChange={setCategorie}
        options={CATEGORIES.map((c) => ({ valeur: c.key, etiquette: c.label }))}
      />

      <div className="grid max-h-[216px] grid-cols-8 gap-0.5 overflow-y-auto">
        {active.emojis.map((e) => (
          <Button
            key={e}
            variant="ghost"
            size="md"
            iconOnly
            onClick={() => onChoisir(e)}
            aria-label={e}
            className="h-9 w-9 text-[20px]"
          >
            <span aria-hidden>{e}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}
